/**
 * Whether the console in this tab is still the one being served, and what to do when a
 * deploy has replaced it.
 *
 * A dispatcher's tab stays open all shift and never reloads by itself, so a deploy at noon
 * reaches nobody until they happen to press F5. Two things follow from that. The tab should
 * say that a newer console exists, which is `newerBuildServed`, asked by `NewBuildNotice`.
 * And a view it has not opened yet asks for a lazily loaded file the new build replaced —
 * the maps are split out that way — which the server no longer has, so the view fails to
 * load at all; `reloadForMissingChunk` answers that by reloading into the new build.
 */

/** The file `vite.config.ts` writes beside every production build. */
const VERSION_FILE = '/version.json';

/** Where this tab remembers when it last reloaded for a missing chunk. */
const RELOADED_AT = 'taxi.missingChunkReloadAt';

/**
 * How long after one reload for a missing chunk another is refused.
 *
 * The reload fetches the new build, which has every file it names. A chunk missing from
 * that as well is a broken deploy, and reloading into it again would loop forever.
 */
const RELOAD_AT_MOST_EVERY_MS = 60_000;

/**
 * True when the server serves a build other than `running`.
 *
 * False for anything short of a clear answer: no stamp in this bundle (the dev server, which
 * replaces modules in place), a network failure, a refusal, or a body that is not the file —
 * under the dev server the single-page fallback answers `/version.json` with `index.html`.
 * A notice that appears on a hiccup teaches people to ignore it.
 *
 * `no-cache` revalidates rather than refetches: nginx answers 304 until a deploy changes
 * the file, so asking every few minutes costs a header.
 */
export async function newerBuildServed(
  running: string | undefined,
  fetcher: typeof fetch = (input, init) => fetch(input, init),
): Promise<boolean> {
  if (!running) return false;

  try {
    const response = await fetcher(VERSION_FILE, { cache: 'no-cache' });
    if (!response.ok) return false;

    const body: unknown = await response.json();
    const served =
      typeof body === 'object' && body !== null && 'build' in body ? body.build : undefined;

    return typeof served === 'string' && served !== '' && served !== running;
  } catch {
    return false;
  }
}

/**
 * Reloads the page for a lazily loaded file the server no longer has, at most once a minute.
 *
 * Returns whether it reloaded. It refuses when this tab cannot remember that it did — with
 * storage blocked, a second failure after the reload would reload again, and a page that
 * reloads forever is worse than one view that failed to open.
 */
export function reloadForMissingChunk(
  storage: () => Storage | undefined,
  reload: () => void,
  now: number = Date.now(),
): boolean {
  try {
    const slot = storage();
    if (slot === undefined) return false;

    const last = Number(slot.getItem(RELOADED_AT));
    if (now - last < RELOAD_AT_MOST_EVERY_MS) return false;

    slot.setItem(RELOADED_AT, String(now));
  } catch {
    return false;
  }

  reload();
  return true;
}
